import { useCallback, useMemo } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CatalogReportDateFilters } from "@/components/catalog/CatalogReportDateFilters";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { catalogReportsApi, type CatalogProductivityRow } from "@/lib/api/catalog-reports";
import { staffApi } from "@/lib/api/staff";
import { useCatalogReportLoader, useCatalogReportQuery } from "@/hooks/useCatalogReportQuery";

type ProductivityTableRow = CatalogProductivityRow & { staffName: string };

function formatStaffName(staffUserId: string, staffNames: Map<string, string>): string {
  return staffNames.get(staffUserId) ?? "Unknown staff";
}

export default function CatalogingProductivityReportPage() {
  const query = useCatalogReportQuery("thisMonth");

  const loadReport = useCallback(async (range: { from?: string; to?: string }) => {
    const [rows, staff] = await Promise.all([
      catalogReportsApi.getProductivity(range),
      staffApi.getAll({ status: "ACTIVE" }),
    ]);
    const staffNames = new Map(
      staff.map((member) => [member.id, `${member.firstName} ${member.lastName}`.trim()])
    );
    return rows.map((row) => ({
      ...row,
      staffName: formatStaffName(row.staffUserId, staffNames),
    }));
  }, []);

  const { data, loading } = useCatalogReportLoader(
    loadReport,
    query.range,
    query.isCustomRangeReady,
    "Unable to load cataloging productivity report."
  );

  const columns = useMemo<DataTableColumn<ProductivityTableRow>[]>(
    () => [
      {
        id: "staff",
        header: "Staff",
        cell: (row) => <span className="font-medium">{row.staffName}</span>,
      },
      {
        id: "books",
        header: "Books",
        cell: (row) => <span className="tabular-nums">{row.book}</span>,
        align: "right",
        width: "w-24",
      },
      {
        id: "journals",
        header: "Journals",
        cell: (row) => <span className="tabular-nums">{row.journal}</span>,
        align: "right",
        width: "w-24",
      },
      {
        id: "eresources",
        header: "E-resources",
        cell: (row) => <span className="tabular-nums">{row.eResource}</span>,
        align: "right",
        width: "w-28",
      },
      {
        id: "total",
        header: "Total",
        cell: (row) => <span className="tabular-nums font-semibold">{row.total}</span>,
        align: "right",
        width: "w-24",
      },
    ],
    []
  );

  const totals = useMemo(() => {
    const rows = data ?? [];
    return rows.reduce(
      (acc, row) => ({
        book: acc.book + row.book,
        journal: acc.journal + row.journal,
        eResource: acc.eResource + row.eResource,
        total: acc.total + row.total,
      }),
      { book: 0, journal: 0, eResource: 0, total: 0 }
    );
  }, [data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cataloging productivity"
        description="Items cataloged by staff, grouped by material type."
        showSearch={false}
      />

      <CatalogReportDateFilters
        preset={query.preset}
        customFrom={query.customFrom}
        customTo={query.customTo}
        onPresetChange={query.setPreset}
        onCustomFromChange={query.setCustomFrom}
        onCustomToChange={query.setCustomTo}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data ?? []}
          rowKey={(row) => row.staffUserId}
          loading={false}
          emptyState={
            <EmptyState
              icon={<BarChart3 className="h-6 w-6 text-slate-400" />}
              title="No cataloging activity"
              description="Staff productivity for the selected period will appear here once items are cataloged."
            />
          }
          footer={
            (data?.length ?? 0) > 0 ? (
              <div className="flex flex-wrap justify-end gap-6 px-4 py-3 text-sm text-slate-700">
                <span>
                  Books: <strong className="tabular-nums">{totals.book}</strong>
                </span>
                <span>
                  Journals: <strong className="tabular-nums">{totals.journal}</strong>
                </span>
                <span>
                  E-resources: <strong className="tabular-nums">{totals.eResource}</strong>
                </span>
                <span>
                  Total: <strong className="tabular-nums">{totals.total}</strong>
                </span>
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
